import React, { useCallback, useMemo } from 'react';

import Close from 'assets/icon/close.svg?react';
import Text from 'components/atoms/text';

import { Spinner } from '../../atoms/spinner';
import FileViewModal from '../fileViewModal';
import DragAndDropFile from './dragAndDrop';
import sm from './styles.module.scss';
import { Props, UseFile } from './types';
import useFile from './useFile';

const File = ({
  prefilledName,
  accept,
  isDisabled,
  onChange,
  value,
  fileDisplayStr,
  onUpdateDocumentOnViewer,
  isUpdateFieldValuePending,
  id,
}: Props) => {
  const {
    fileRef,
    showFileModal,
    fileName,
    deleteFileHandler,
    addFileMediator,
    fileViewerInfo,
    handleShowFileModal,
  }: UseFile = useFile(value, onChange, fileDisplayStr, accept);

  // region ********************************JSX*********************************
  const renderDragAndDropUpload = useCallback(
    () => (
      <>
        <DragAndDropFile
          isDisabled={isDisabled}
          addFileHandler={addFileMediator}
          fileRef={fileRef}
        />
        <input
          ref={fileRef}
          onChange={
            addFileMediator as unknown as React.ChangeEventHandler<HTMLInputElement>
          }
          className={sm.File}
          id={id}
          type="file"
          value=""
          disabled={isDisabled || !!value}
          accept={accept.join(',')}
        />
      </>
    ),
    [isDisabled, addFileMediator, fileRef, id, value, accept]
  );

  const renderUploadFile = useCallback(
    () => <div className={sm.FileWrapper}>{renderDragAndDropUpload()}</div>,
    [renderDragAndDropUpload]
  );

  const renderFileValue = useCallback(
    () => (
      <div className={sm.FileTitle}>
        <>
          {isUpdateFieldValuePending && <Spinner />}
          {fileViewerInfo && (
            <FileViewModal
              isVisible={showFileModal}
              onCloseClick={() => handleShowFileModal(false)}
              fileInformation={fileViewerInfo}
              fileId={'file'}
              onUpdateDocument={
                isDisabled ? undefined : onUpdateDocumentOnViewer
              }
              usePrivateViewer
            />
          )}
          <button
            type="button"
            className={sm.TextLink}
            onClick={() => handleShowFileModal(true)}
          >
            <Text content={fileName} withBlueColor={!!value} with15Font />
          </button>
        </>
        <button
          type="button"
          className={sm.DeleteFileBtn}
          onClick={isDisabled ? undefined : deleteFileHandler}
        >
          {!isDisabled && <Close />}
        </button>
      </div>
    ),
    [
      isUpdateFieldValuePending,
      fileViewerInfo,
      showFileModal,
      onUpdateDocumentOnViewer,
      fileName,
      value,
      isDisabled,
      deleteFileHandler,
      handleShowFileModal,
    ]
  );

  const hasValue = useMemo(
    () => value || prefilledName,
    [value, prefilledName]
  );

  return hasValue ? renderFileValue() : renderUploadFile();
  // endregion
};

export default File;
